import { Fragment, useEffect, useState } from "react";

import {
  TextField,
  Button,
  Typography,
  Select,
  AutocompleteMultipleSelect,
  Checkbox,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Row, Col, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  getInstitutionsAndFounders,
  getLocationLabel,
  getLocations,
  getRemovedFounder,
  getUserRole,
} from "./utils";
import { getCountyList } from "../../api/Geography/GeographyApi";
import { searchInstitutionNew } from "../../api/Institution/InstitutionApi";
import {
  getUserById,
  saveUser,
  getUserOibServiceData,
  getRoleList,
} from "../../api/User/UserApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import {
  ValidationFailure,
  ServiceResultStatus,
  Role as RoleEnum,
} from "../../models";
import {
  InstitutionCodeBook,
  InstitutionCodeBookSearch,
} from "../../models/Institution";
import { User, ClaimType, Role } from "../../models/User";
import {
  FounderCodeBook,
  useSearchFoundersQuery,
} from "../../queries/founderQuery";
import { userDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";

export function UserEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const oibRegex = /^\d{11}$/;

  const createDefaultItem = (): User => ({
    id: 0,
    oib: "",
    firstName: "",
    lastName: "",
    userRoles: [],
    oibValidated: false,
  });

  const [item, setItem] = useState<User>(createDefaultItem());
  const [roles, setRoles] = useState<Role[]>([]);
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [oibSearched, setOibSearched] = useState<boolean>(false);
  // Counties are filtered on load when the current user is at
  // the County level.
  const [counties, setCounties] = useState<CodeBook[]>();
  // Institutions are filtered on search when the current user is
  // at the Institution level.
  const [filteredInstitutions, setFilteredInstitutions] = useState<
    InstitutionCodeBook[]
  >([]);
  const [founderSearchTerm, setFounderSearchTerm] = useState<string>("");

  const handleFounderFilterChange = (filter: string): Promise<void> => {
    setTimeout(() => setFounderSearchTerm(filter), 300);
    return Promise.resolve(); // Needed to satisfy the return type of the function
  };

  const { data: founders } = useSearchFoundersQuery(founderSearchTerm);

  useEffect(() => {
    (async () => {
      const fetchedRoles = await getRoleList();
      setRoles(fetchedRoles);

      if (auth.currentRole && id) {
        const result = await getUserById(+id);
        if (result) {
          setItem(result);

          if (auth.currentRole.claimType !== ClaimType.Institution) {
            const countyList = await getCountyList();
            const filteredCounties =
              auth.currentRole.claimType === ClaimType.County
                ? countyList.filter((val) =>
                    auth.currentRole!.locations?.find((l) => l === val.id)
                  )
                : countyList;
            setCounties(filteredCounties);
          }
        }
      }
    })();
    dispatch(resetIsEdited());
  }, [id, auth.currentRole, dispatch]);

  const handleOibChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oib = e.target.value;
    setItem({
      ...item,
      oib,
      oibValidated: false,
      firstName: undefined,
      lastName: undefined,
    });
    dispatch(setIsEdited());
  };

  const handleEmailChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleEmailChangedString(e.target.value);
  };

  const handleEmailChangedString = (value: string | undefined) => {
    setItem({
      ...item,
      email: value && value.length > 0 ? value : undefined,
    });
    dispatch(setIsEdited());
  };

  const handleTelephoneChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleTelephoneChangedString(e.target.value);
  };

  const handleTelephoneChangedString = (value: string | undefined) => {
    setItem({
      ...item,
      telephone: value && value.length > 0 ? value : undefined,
    });
    dispatch(setIsEdited());
  };

  const handleOibSystem = async () => {
    if (item.oib && oibRegex.test(item.oib)) {
      const result = await getUserOibServiceData(item.oib);
      setOibSearched(true);
      if (result?.status === ServiceResultStatus.Success && result.item) {
        setItem({
          ...item,
          id: result.item.id ?? 0,
          firstName: result.item.firstName ?? "",
          lastName: result.item.lastName ?? "",
          oibValidated: true,
        });
        dispatch(setIsEdited());
      }
    }
  };

  const handleInstitutionFilterChange = async (filter: string) => {
    // If the current role is at the Institution level, filter by
    // available locations. The county level should never call this
    // function. The global level has no restrictions.
    const search: InstitutionCodeBookSearch = {
      search: filter,
      onlyActive: false,
      includeOutsideEnrollmentArea: true,
      countyIds:
        auth.currentRole?.claimType === ClaimType.County
          ? auth.currentRole.locations
          : undefined,
      ids:
        auth.currentRole?.claimType === ClaimType.Institution
          ? auth.currentRole.locations
          : undefined,
    };
    const result = await searchInstitutionNew(search);
    setFilteredInstitutions(result);
  };

  const handleRoleChangeBoolean = (roleId: number, enabled: boolean) => {
    // Only called for Global roles.
    // If the current user is at the Institution level or the
    // County level, this function should never be called.
    // The global level has no restrictions.
    if (enabled) {
      const role = roles.find((val) => val.id === roleId);
      if (role) {
        const userRoles = [...item.userRoles];
        userRoles.push(getUserRole({ role }));

        setItem({ ...item, userRoles });
        dispatch(setIsEdited());
      }
    } else {
      const userRoles = item.userRoles.filter((val) => val.role.id !== roleId);
      setItem({ ...item, userRoles });
      dispatch(setIsEdited());
    }
  };

  const handleRoleChangeNumber = (
    roleId: number,
    locations: number[] | undefined
  ) => {
    // Only called from the County multi-select.
    // If the current user is at the County level, the available
    // counties are already filtered. The Institution level should never call
    // this function. The global level has no restrictions.
    if (locations) {
      const userRoles = [...item.userRoles];
      let userRole = userRoles.find((val) => val.role.id === roleId);

      if (!userRole) {
        const role = roles.find((val) => val.id === roleId);
        if (role) {
          userRole = getUserRole({ role });
          userRoles.push(userRole);
        }
      }

      if (userRole) {
        userRole.locations = locations.map<CodeBook>((val) => ({
          id: val,
          name: "",
        }));
        setItem({ ...item, userRoles });
        dispatch(setIsEdited());
      }
    } else {
      const userRoles = item.userRoles.filter((val) => val.role.id !== roleId);
      setItem({ ...item, userRoles });
      dispatch(setIsEdited());
    }
  };

  const handleRoleChangeCodeBook = async (
    roleId: number,
    locations: CodeBook[] | FounderCodeBook[] // The array containing the selection
  ) => {
    const isFounder = roleId === RoleEnum.Founder;

    let userRoles = [...item.userRoles];

    // Check if the selected role exists in the userRoles array
    let userRole = userRoles.find((val) => val.role.id === roleId);

    if (!userRole) {
      const role = roles.find((val) => val.id === roleId);

      if (role) {
        if (isFounder) {
          const typedLocations = locations as FounderCodeBook[];

          const { institutions, founders } =
            getInstitutionsAndFounders(typedLocations);

          userRole = getUserRole({
            role,
            locations: institutions,
            founders,
          });
        } else {
          userRole = getUserRole({
            role,
            locations,
          });
        }

        userRoles.push(userRole);
      }
    } else {
      if (isFounder) {
        const typedLocations = locations as FounderCodeBook[];

        const removedFounder = getRemovedFounder(
          userRole.founders,
          typedLocations
        );

        const selectedFounders = typedLocations.map((location) => ({
          id: location.id,
          name: location.name,
        }));

        const mergedLocations = await getLocations({
          currentLocations: userRole.locations,
          typedLocations,
          removedFounder,
        });

        userRole.founders = selectedFounders;
        userRole.locations = mergedLocations;

        // Remove the role if both arrays are empty
        if (!selectedFounders.length && !mergedLocations.length) {
          userRoles = userRoles.filter((role) => role !== userRole);
        }
      } else {
        userRole.locations = locations as CodeBook[];
      }
    }

    setItem({ ...item, userRoles });
    dispatch(setIsEdited());
  };

  /**
   * Renders the possible UserRoles which are assignable by the current user.
   * @returns
   */
  const renderUserRoles = () => (
    <Row>
      {roles.reduce((acc, val) => {
        if (
          auth.currentRole?.rolePermissions?.find((r) => r === val.id) !==
          undefined
        ) {
          acc.push(
            <Fragment key={val.id}>
              <Col md={4}>{renderRole(val, item)}</Col>
              <Col md={8}>{renderLocations(val, item)}</Col>
            </Fragment>
          );
        }
        return acc;
      }, [] as JSX.Element[])}
    </Row>
  );
  /**
   * Renders the role which is assignable by the current user for the specified
   * user.
   * @param role The role which is assignable by the current user.
   * @param item The edited user to which the role may apply.
   * @returns
   */
  const renderRole = (role: Role, item: User) => {
    if (role.claimTypeId === ClaimType.Global) {
      return (
        <Checkbox
          checked={
            item.userRoles.find((ur) => ur.role.id === role.id) !== undefined
          }
          label={role.name}
          disabled={!role.isEditable}
          onChange={(e) => handleRoleChangeBoolean(role.id, e.target.checked)}
        />
      );
    }
    return <Typography color="light">{role.name}</Typography>;
  };

  /**
   * Renders the locations which are assignable by the current user for the specified
   * user.
   * @param role The role which is assignable by the current user.
   * @param item The edited user to which the role may apply.
   * @returns
   */
  const renderLocations = (role: Role, item: User) => {
    if (role.id === RoleEnum.Founder) {
      const label = getLocationLabel(role);

      return (
        <AutocompleteMultipleSelect<CodeBook>
          label={label}
          name={role.name}
          value={
            item.userRoles.find((r) => r.role.id === role.id)?.founders ?? []
          }
          valueProp="id"
          textProp="name"
          onChange={(e) => handleRoleChangeCodeBook(role.id, e.target.value)}
          selectOptions={founders ?? []}
          onFilterChange={handleFounderFilterChange}
          maximumSelectedItems={50}
        />
      );
    }

    if (
      [
        RoleEnum.CountyCommittee,
        RoleEnum.InstitutionCommittee,
        RoleEnum.RegionalCommittee,
      ].includes(role.id)
    ) {
      const label = getLocationLabel(role);

      return (
        <TextField
          disabled
          label={label}
          readonly
          value={item.userRoles
            .find((r) => r.role.id === role.id)
            ?.locations?.map((l) => l.name)
            .join(", ")}
        />
      );
    }

    if (role.claimTypeId === ClaimType.County) {
      return (
        <Select
          multiple
          label={t("common.schoolCandidateModel.countyList")}
          name={role.name}
          value={item.userRoles
            .find((r) => r.role.id === role.id)
            ?.locations?.map((l) => l.id)}
          onChange={(e) => handleRoleChangeNumber(role.id, e.target.value)}
          selectData={codeBookToSelectData(counties)}
          disabled={!role.isEditable}
        />
      );
    }

    if (role.claimTypeId === ClaimType.Institution) {
      return (
        <AutocompleteMultipleSelect<CodeBook>
          label={t("common.schoolCandidateModel.institutionList")}
          name={role.name}
          value={
            item.userRoles.find((r) => r.role.id === role.id)?.locations ?? []
          }
          valueProp="id"
          textProp="name"
          onChange={(e) => handleRoleChangeCodeBook(role.id, e.target.value)}
          selectOptions={filteredInstitutions}
          onFilterChange={handleInstitutionFilterChange}
          maximumSelectedItems={50}
        />
      );
    }
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd="hide"
        showBack="show"
        showEdit="hide"
        pageTitle={t("common.user.pageNameDetails")}
        errors={errors}
      />
      <FormCard>
        <Row>
          <Col md={2}>
            <TextField
              label={t("common.user.oib")}
              name="item.oib"
              value={item.oib}
              onChange={handleOibChanged}
              errorText={errors?.find((e) => e.propertyName === "OIB")?.message}
              plaintext={id !== "0"}
            />
          </Col>
          <Col md={5}>
            <Button
              onClick={handleOibSystem}
              disabled={!item.oib || !oibRegex.test(item.oib)}
            >
              {t("common.user.connectOibSystem")}
            </Button>
          </Col>
          <Col md={5}>
            <Alert
              dismissible={false}
              show={oibSearched || id !== "0"}
              variant={item.oibValidated ? "success" : "danger"}
            >
              {item.oibValidated
                ? t("common.user.oibSuccess")
                : t("common.user.oibFailure")}
            </Alert>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.user.lastName")}
              name="item.lastName"
              value={item.lastName}
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.user.firstName")}
              name="item.firstName"
              value={item.firstName}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.email")}
              name="item.email"
              value={item.email}
              onChange={handleEmailChanged}
              required
              min={6}
              max={250}
              errorText={
                errors?.find((e) => e.propertyName === "Email")?.message
              }
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.phone")}
              name="item.telephone"
              value={item.telephone}
              onChange={handleTelephoneChanged}
              min={6}
              max={250}
              errorText={
                errors?.find((e) => e.propertyName === "Telephone")?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Typography color="light">{t("common.user.roles")}</Typography>
          </Col>
        </Row>
        {renderUserRoles()}
      </FormCard>

      <ToolbarEdit
        showCancel="show"
        showSave={item.oibValidated ? "show" : "disable"}
        redirectUrlSaveOK={userDetailUrl}
        handleSave={async () => saveUser(item)}
        setSaveErrors={setErrors}
      />
    </ContentArea>
  );
}
